// Import Dependencies
import { Fragment, useState } from "react";
import clsx from "clsx";
import {
  Dialog,
  DialogPanel,
  DialogTitle,
  Transition,
  TransitionChild,
} from "@headlessui/react";
import { XMarkIcon, MagnifyingGlassIcon, PlusIcon, PencilIcon, TrashIcon, DocumentTextIcon, EyeIcon, InformationCircleIcon } from "@heroicons/react/24/outline";
import {
  FiType,
  FiMail,
  FiPhone,
  FiHash,
  FiCalendar,
  FiChevronDown,
  FiFileText,
  FiCheckSquare,
  FiCircle,
  FiPaperclip,
  FiGlobe,
  FiClock,
  FiImage,
  FiStar,
  FiSliders,
  FiMapPin,
  FiDollarSign,
  FiPercent
} from "react-icons/fi";

// Local Imports
import { Button, Input } from "components/ui";
import { CreateMasterFieldModal } from "./CreateMasterFieldModal";
import { MasterFieldPreviewModal } from "./MasterFieldPreviewModal";
import { deleteMasterField } from "services/formBuilderService";
import { ConfirmModal } from "components/shared/ConfirmModal";
import { hasPermission } from "utils/permissionsUtils";

// Available field types with React Icons
const fieldTypes = [
  { value: "text", label: "Text Input", icon: FiType, description: "Single line text input" },
  { value: "email", label: "Email", icon: FiMail, description: "Email address input with validation" },
  { value: "tel", label: "Phone Number", icon: FiPhone, description: "Phone number input" },
  { value: "number", label: "Number", icon: FiHash, description: "Numeric input only" },
  { value: "date", label: "Date", icon: FiCalendar, description: "Date picker input" },
  { value: "select", label: "Dropdown", icon: FiChevronDown, description: "Single select dropdown" },
  { value: "textarea", label: "Text Area", icon: FiFileText, description: "Multi-line text input" },
  { value: "checkbox", label: "Checkbox Group", icon: FiCheckSquare, description: "Multiple selection checkboxes" },
  { value: "radio", label: "Radio Buttons", icon: FiCircle, description: "Single selection radio buttons" },
  { value: "file", label: "File Upload", icon: FiPaperclip, description: "File upload input" },
  { value: "url", label: "Website URL", icon: FiGlobe, description: "URL input with validation" },
  { value: "time", label: "Time", icon: FiClock, description: "Time picker input" },
  { value: "image", label: "Image Upload", icon: FiImage, description: "Image file upload with preview" },
  { value: "rating", label: "Rating", icon: FiStar, description: "Star rating input" },
  { value: "range", label: "Slider", icon: FiSliders, description: "Range slider input" },
  { value: "address", label: "Address", icon: FiMapPin, description: "Address input with autocomplete" },
  { value: "currency", label: "Currency", icon: FiDollarSign, description: "Currency amount input" },
  { value: "percentage", label: "Percentage", icon: FiPercent, description: "Percentage input" }
];

// Map HTML input types to React Icons
const getIconForInputType = (inputType) => {
  const iconMap = {
    text: FiType,
    email: FiMail,
    tel: FiPhone,
    number: FiHash,
    date: FiCalendar,
    select: FiChevronDown,
    textarea: FiFileText,
    checkbox: FiCheckSquare,
    radio: FiCircle,
    file: FiPaperclip,
    url: FiGlobe,
    time: FiClock,
    image: FiImage,
    rating: FiStar,
    range: FiSliders,
    location: FiMapPin,
    currency: FiDollarSign,
    percentage: FiPercent,
    password: FiType
  };

  return iconMap[inputType] || FiType;
};

// Map HTML input types to descriptions
const getDescriptionForInputType = (inputType) => {
  const descriptionMap = {
    text: "Single line text input",
    email: "Email address input with validation",
    tel: "Phone number input",
    number: "Numeric input only",
    date: "Date picker input",
    select: "Single select dropdown",
    textarea: "Multi-line text input",
    checkbox: "Multiple selection checkboxes",
    radio: "Single selection radio buttons",
    file: "File upload input",
    url: "URL input with validation",
    time: "Time picker input",
    image: "Image file upload with preview",
    rating: "Star rating input",
    range: "Range slider input",
    location: "Location/address input",
    currency: "Currency amount input",
    percentage: "Percentage input",
    password: "Password input field"
  };

  return descriptionMap[inputType] || "Input field";
};

// ----------------------------------------------------------------------

export function FieldSelectionModal({
  isOpen,
  onClose,
  onSelectField,
  fieldTypes: propFieldTypes = null,
  masterFields = [],
  masterFieldsLoading = false,
  masterFieldsError = null,
  loading = false,
  error = null,
  onCreateField,
  onRefreshMasterFields,
  onEditField,
  onDeleteField,
  formConfig = { steps: [], fields: [] },
  isMultistep = false
}) {
  const [searchQuery, setSearchQuery] = useState("");
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [selectedCategoryId, setSelectedCategoryId] = useState(null);
  const [editingField, setEditingField] = useState(null);
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
  const [fieldToDelete, setFieldToDelete] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deleteState, setDeleteState] = useState("pending");
  const [isPreviewModalOpen, setIsPreviewModalOpen] = useState(false);
  const [previewField, setPreviewField] = useState(null);
  const handleCreateFieldSuccess = (createdField) => {
    // Refresh master fields list to include the newly created field
    onRefreshMasterFields && onRefreshMasterFields();
    
    // Reset any state
    setEditingField(null);
    setIsCreateModalOpen(false);
  };

  const handleEditFieldClick = (masterField) => {
    setEditingField(masterField);
    setSelectedCategoryId(masterField.CategoryId);
    setIsCreateModalOpen(true);
  };

  const handleViewFieldClick = (masterField) => {
    setPreviewField(masterField);
    setIsPreviewModalOpen(true);
  };

  const handleClosePreviewModal = () => {
    setIsPreviewModalOpen(false);
    setPreviewField(null);
  };

  const handleCloseCreateModal = () => {
    setIsCreateModalOpen(false);
    setEditingField(null);
  };

  const handleDeleteFieldClick = (masterField) => {
    setFieldToDelete(masterField);
    setDeleteState("pending");
    setIsConfirmModalOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!fieldToDelete) return;

    setDeleteLoading(true);
    try {
      const result = await deleteMasterField(fieldToDelete.Id);

      if (result.success) {
        setDeleteState("success");
        // Refresh master fields after successful deletion
        setTimeout(() => {
          onRefreshMasterFields && onRefreshMasterFields();
          setIsConfirmModalOpen(false);
          setFieldToDelete(null);
          setDeleteLoading(false);
        }, 1500);
      } else {
        setDeleteState("error");
        setDeleteLoading(false);
      }
    } catch (error) {
      console.error("Error deleting master field:", error);
      setDeleteState("error");
      setDeleteLoading(false);
    }
  };

  const handleCloseConfirmModal = () => {
    if (!deleteLoading) {
      setIsConfirmModalOpen(false);
      setFieldToDelete(null);
      setDeleteState("pending");
    }
  };

  const handleFieldSelect = (fieldType) => {
    onSelectField(fieldType);
    onClose();
  };

  const handleMasterFieldSelect = (masterField) => {
    const masterIdRef = masterField.Id || masterField.id;
    // Convert master field to field format with masterFieldId
    const fieldData = {
      fieldLabel: masterField.FieldLabel,
      fieldTypeId: masterField.FieldTypeId,
      fieldName: masterField.FieldName,
      placeholder: masterField.Placeholder,
      defaultValue: masterField.DefaultValue,
      masterFieldId: masterIdRef,
      MasterFieldId: masterIdRef,
      isMasterField: true,
      htmlInputType: masterField.HtmlInputType || masterField.htmlInputType,
      options: masterField.MasterFormFieldOptions || masterField.options || []
    };
    onSelectField(fieldData);
    onClose();
  };

  // Get the location of currently added master fields
  const fieldLocations = new Map();
  if (isMultistep) {
    (formConfig.steps || []).forEach((step, index) => {
      (step.fields || []).forEach(field => {
        const mId = field.masterFieldId || field.MasterFieldId;
        if (mId) {
          fieldLocations.set(String(mId), step.stepTitle || `Step ${index + 1}`);
        }
      });
    });
  } else {
    (formConfig.fields || []).forEach(field => {
      const mId = field.masterFieldId || field.MasterFieldId;
      if (mId) fieldLocations.set(String(mId), "this form");
    });
  }

  // Use provided field types or fall back to defaults
  const availableFieldTypes = propFieldTypes || fieldTypes;

  // Transform API field types to include React icons
  const processedFieldTypes = availableFieldTypes.map(fieldType => ({
    ...fieldType,
    icon: getIconForInputType(fieldType.value),
    description: getDescriptionForInputType(fieldType.value)
  }));

  // Filter master fields based on search query
  const filteredMasterFields = masterFields.map(category => {
    if (!searchQuery.trim()) {
      return category;
    }

    const query = searchQuery.toLowerCase();
    const filteredFields = category.MasterFields?.filter(field =>
      field.FieldLabel?.toLowerCase().includes(query) ||
      field.FieldName?.toLowerCase().includes(query) ||
      category.CategoryName?.toLowerCase().includes(query)
    ) || [];

    return {
      ...category,
      MasterFields: filteredFields
    };
  }).filter(category => category.MasterFields && category.MasterFields.length > 0);

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog
        as="div"
        className="fixed inset-0 z-[110] flex flex-col items-center justify-center overflow-hidden px-2 py-4 sm:px-4 sm:py-6"
        onClose={() => { }}
      >
        <TransitionChild
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="absolute inset-0 bg-gray-900/50 backdrop-blur transition-opacity dark:bg-black/30" />
        </TransitionChild>

        <TransitionChild
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0 scale-95"
          enterTo="opacity-100 scale-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100 scale-100"
          leaveTo="opacity-0 scale-95"
        >
          <DialogPanel className="relative flex w-full max-w-4xl max-h-[90vh] origin-top flex-col overflow-hidden rounded-lg bg-white transition-all duration-300 dark:bg-dark-700 mx-4 sm:mx-0">
            {/* Header */}
            <div className="rounded-t-lg bg-gray-200 px-4 sm:px-6 py-4 dark:bg-dark-800">
              <div className="flex items-center justify-between gap-2">
                <div className="flex-1 min-w-0">
                  <DialogTitle
                    as="h3"
                    className="text-base sm:text-lg font-semibold text-gray-800 dark:text-dark-100"
                  >
                    Master Fields
                  </DialogTitle>
                  <p className="text-xs sm:text-sm text-gray-600 dark:text-dark-300 mt-1">
                    Choose a master field to add to your form or create a new one
                  </p>
                </div>
                <Button
                  onClick={onClose}
                  variant="flat"
                  isIcon
                  className="size-8 rounded-full cursor-pointer flex-shrink-0"
                >
                  <XMarkIcon className="size-5" />
                </Button>
              </div>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto max-h-[70vh] beautiful-scrollbar p-4 sm:p-6">
              {/* Search Input for Master Fields */}
              <div className="mb-4 sm:mb-6">
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
                  </div>
                  <Input
                    type="text"
                    placeholder="Search master fields..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-10 w-full"
                  />
                </div>
                {searchQuery && (
                  <div className="mt-2 flex items-center justify-between text-sm text-gray-600 dark:text-dark-300">
                    <span>
                      {filteredMasterFields.reduce((sum, cat) => sum + (cat.MasterFields?.length || 0), 0)} field(s) found
                    </span>
                    <button
                      onClick={() => setSearchQuery("")}
                      className="text-primary-600 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300 font-medium"
                    >
                      Clear search
                    </button>
                  </div>
                )}
              </div>

              {/* Master Fields Content - Loading state removed, handled by global loader */}
              {masterFieldsError ? (
                <div className="flex items-center justify-center py-12">
                  <div className="text-center">
                    <div className="text-red-500 mb-4">⚠️</div>
                    <p className="text-red-600 dark:text-red-400 mb-2">Failed to load master fields</p>
                    <p className="text-sm text-gray-600 dark:text-dark-300">{masterFieldsError}</p>
                  </div>
                </div>
              ) : masterFields.length === 0 ? (
                <div className="flex items-center justify-center py-12">
                  <div className="text-center">
                    <div className="mx-auto mb-4 h-16 w-16 rounded-full bg-gray-100 dark:bg-dark-700 flex items-center justify-center">
                      <DocumentTextIcon className="h-8 w-8 text-gray-400" />
                    </div>
                    <p className="text-gray-600 dark:text-dark-300 font-medium">No master fields available</p>
                    <p className="text-sm text-gray-500 dark:text-dark-400 mt-1">Create a master field to reuse it across forms</p>
                  </div>
                </div>
              ) : filteredMasterFields.length === 0 ? (
                <div className="flex items-center justify-center py-12">
                  <div className="text-center">
                    <MagnifyingGlassIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                    <p className="text-gray-600 dark:text-dark-300 mb-2">No fields found</p>
                    <p className="text-sm text-gray-500 dark:text-dark-400">
                      Try adjusting your search terms
                    </p>
                  </div>
                </div>
              ) : (
                <div className="space-y-4 sm:space-y-6">
                  {filteredMasterFields.map((category) => (
                    <div key={category.Id}>
                      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-3 gap-2">
                        <h3 className="text-base sm:text-lg font-semibold text-gray-900 dark:text-dark-100">
                          {category.CategoryName}
                        </h3>
                        {hasPermission("FBCF") ? <Button
                          variant="flat"
                          size="sm"
                          onClick={() => {
                            setSelectedCategoryId(category.Id);
                            setIsCreateModalOpen(true);
                          }}
                          className="flex items-center gap-2 text-primary-600 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300 cursor-pointer self-start sm:self-auto"
                        >
                          <PlusIcon className="w-4 h-4" />
                          <span className="text-sm">Create New Field</span>
                        </Button> : null}
                      </div>
                      {category.MasterFields && category.MasterFields.length > 0 ? (
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
                          {category.MasterFields.map((masterField) => {
                            const IconComponent = getIconForInputType('text'); // Default icon for master fields
                            const isCompanyMasterField = masterField.IsCompanyMasterField === 1;
                            const masterIdRef = masterField.Id || masterField.id;
                            const isAlreadyAdded = fieldLocations.has(String(masterIdRef));
                            const location = fieldLocations.get(String(masterIdRef));

                            return (
                              <div key={masterField.Id} className="h-full">
                                <div
                                  className={clsx(
                                    "relative flex flex-1 flex-col items-start p-3 sm:p-4 h-full min-h-[105px] sm:min-h-[120px] text-left transition-all border rounded-lg group overflow-hidden",
                                    isAlreadyAdded
                                      ? "bg-gray-50/50 dark:bg-red-900/10 border-gray-200 dark:border-dark-700 cursor-not-allowed shadow-none"
                                      : "bg-white dark:bg-dark-700 hover:bg-gray-100 dark:hover:bg-dark-600 border-gray-200 dark:border-dark-600 cursor-pointer shadow-sm hover:shadow-md"
                                  )}
                                  onClick={() => !isAlreadyAdded && handleMasterFieldSelect(masterField)}
                                >
                                  {/* Red Blur Overlay for Already Added Fields */}
                                  {isAlreadyAdded && (
                                    <div className="absolute inset-0 z-10 flex flex-col items-center justify-center bg-white/40 dark:bg-dark-900/60 p-4 text-center">
                                      <div className="w-10 h-10 mb-2 rounded-full bg-red-100 dark:bg-red-900/50 flex items-center justify-center text-red-600 dark:text-red-400 shadow-sm animate-pulse">
                                        <InformationCircleIcon className="w-6 h-6" />
                                      </div>
                                      <span className="text-[11px] font-black text-red-500 dark:text-red-400 uppercase tracking-tight leading-tight">
                                        This field is already used in this form
                                        {location !== 'this form' && (
                                          <span className="block mt-1 normal-case text-[10px] font-bold text-red-500/80">({location})</span>
                                        )}
                                      </span>
                                    </div>
                                  )}

                                  <div className={clsx("w-full transition-all duration-300", isAlreadyAdded && "opacity-50 grayscale-[0.5]")}>
                                    <div className="flex items-start space-x-3 mb-3 sm:mb-4 w-full">
                                      <div className="flex-shrink-0 p-2 rounded-lg bg-green-100 dark:bg-green-900 group-hover:bg-green-200 dark:group-hover:bg-green-800 transition-colors">
                                        <IconComponent className="size-4 sm:size-5 text-green-600 dark:text-green-400" />
                                      </div>
                                      <div className="flex-1 min-w-0">
                                        <h4 className="font-medium truncate text-sm sm:text-base mb-1 text-gray-900 dark:text-dark-100">
                                          {masterField.FieldLabel}
                                        </h4>
                                        <span className="text-xs font-bold uppercase tracking-wider text-green-600 dark:text-green-400">
                                          Master Field
                                        </span>
                                      </div>
                                    </div>
                                    <p className="text-xs sm:text-sm leading-relaxed mb-3 sm:mb-4 text-gray-600 dark:text-dark-300">
                                      Name: {masterField.FieldName}
                                    </p>
                                    <div className="flex items-center justify-between w-full pt-2 mt-auto border-t border-gray-100 dark:border-dark-600">
                                      {isCompanyMasterField ? (
                                        <span className="text-xs bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300 px-2 py-1 rounded-full font-medium border border-blue-200 dark:border-blue-800">
                                          Custom
                                        </span>
                                      ) : (
                                        <span className="text-xs bg-gray-100 text-gray-700 dark:bg-dark-600 dark:text-dark-300 px-2 py-1 rounded-full font-medium border border-gray-200 dark:border-dark-500">
                                          System
                                        </span>
                                      )}
                                      <div className="flex items-center gap-1">
                                        <button
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            handleViewFieldClick(masterField);
                                          }}
                                          className="p-1 text-gray-400 hover:text-green-600 dark:hover:text-green-400 transition-colors rounded-full hover:bg-gray-100 dark:hover:bg-dark-600"
                                          title="View field preview"
                                        >
                                          <EyeIcon className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                                        </button>
                                        {isCompanyMasterField && hasPermission("FBEF") ? <button
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            handleEditFieldClick(masterField);
                                          }}
                                          className="p-1 text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 transition-colors rounded-full hover:bg-gray-100 dark:hover:bg-dark-600"
                                          title="Edit field"
                                        >
                                          <PencilIcon className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                                        </button> : null}
                                        {isCompanyMasterField && hasPermission("FBDF") ? <button
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            handleDeleteFieldClick(masterField);
                                          }}
                                          className="p-1 text-gray-400 hover:text-red-600 dark:hover:text-red-400 transition-colors rounded-full hover:bg-gray-100 dark:hover:bg-dark-600"
                                          title="Delete field"
                                        >
                                          <TrashIcon className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                                        </button> : null}
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      ) : (
                        <p className="text-sm text-gray-500 dark:text-dark-400 italic">
                          No fields in this category
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="border-t border-gray-200 dark:border-dark-600 px-4 sm:px-6 py-4">
              <div className="flex justify-end">
                <Button
                  onClick={onClose}
                  variant="outlined"
                  className="min-w-[100px] cursor-pointer"
                >
                  Cancel
                </Button>
              </div>
            </div>
          </DialogPanel>
        </TransitionChild>
      </Dialog>

      {/* Create/Edit Master Field Modal */}
      <CreateMasterFieldModal
        isOpen={isCreateModalOpen}
        onClose={handleCloseCreateModal}
        categoryId={selectedCategoryId}
        fieldTypes={propFieldTypes || []}
        onSuccess={handleCreateFieldSuccess}
        existingField={editingField}
      />

      {/* Master Field Preview Modal */}
      <MasterFieldPreviewModal
        isOpen={isPreviewModalOpen}
        onClose={handleClosePreviewModal}
        masterField={previewField}
      />

      {/* Confirm Delete Modal */}
      {isConfirmModalOpen && (
        <ConfirmModal
          show={isConfirmModalOpen}
          onClose={handleCloseConfirmModal}
          onOk={handleConfirmDelete}
          confirmLoading={deleteLoading}
          state={deleteState}
          buttonColor="error"
          messages={{
            pending: {
              title: "Delete Master Field?",
              description: `Are you sure you want to delete "${fieldToDelete?.FieldLabel}"? This action cannot be undone.`,
              actionText: "Delete",
            },
            success: {
              title: "Field Deleted",
              description: "The master field has been successfully deleted.",
              actionText: "Done",
            },
            error: {
              title: "Delete Failed",
              description: "Failed to delete the master field. Please try again.",
              actionText: "Retry",
            },
          }}
        />
      )}
    </Transition>
  );
}
